import React, { useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { styled } from '@mui/material/styles';
import { AppImage, useStacklineTheme } from '@stackline/ui';
import { useAppSelector } from 'src/utils/Hooks';
import _flatten from 'lodash/flatten';
import _cloneDeep from 'lodash/cloneDeep';
import { History, Location } from 'history';
import { FormControlLabel, Divider, TextField, Checkbox, Radio } from '@mui/material';
import { ProductGrowthFilterModalHeader } from 'src/components/BeaconV2Components/ProductGrowthModal/ModalComponents/ProductGrowthFilterModal';
import {
  FilterKey,
  generateFilterUrlString,
  validMinMaxRetailPrice
} from 'src/components/BeaconV2Components/common/AppFilterButton/utils';
import { SelectedIncludeCheckbox } from 'src/components/SvgIcons';
import './appFilterStyles.scss';
import { BusinessUnit, CategoryEntity, Segment, SubCategoryEntity, BrandEntity } from 'sl-api-connector';
import { SlRow } from 'src/components/BeaconV2Components/Generic/SlRow';
import { adjustmentStatusOptions } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/filters/ForecastFilterButton';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import QuadrantFilterSection, {
  ProductGrowthQuadrant,
  MatchTypeFilterSection
} from 'src/components/BeaconV2Components/ProductGrowthModal/ModalComponents/QuadrantFilterSection';
import { Box } from '@mui/system';
import BusinessUnitFilterGroup from 'src/components/BeaconV2Components/common/AppFilterButton/BusinessUnitFilterSection';
import _uniqBy from 'lodash/uniqBy';
import { useMainEntityId, useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { shouldShowNewAtlas } from 'src/utils/app';
import RetailPriceFilterSection from 'src/components/BeaconV2Components/common/AppFilterButton/RetailPriceFilterSection';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { VoiceAndToneMatchType } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import { useQueryClient } from '@tanstack/react-query';
import StyledCheckbox from 'src/components/BeaconV2Components/common/StyledCheckbox';

const GenericStyledDialogue = styled(Dialog)({
  '& .MuiPaper-root': {
    width: '752px',
    height: '641px',
    boxShadow: '0 0 16px 0 rgba(0, 0, 0, 0.06)',
    border: 'solid 1px #dedede',
    backgroundColor: '#fff',
    borderRadius: '8px'
  }
});

export const AppFilterModalFooter = ({
  handleApplyFilters,
  handleClearFilters
}: {
  handleApplyFilters: () => void;
  handleClearFilters: () => void;
}) => {
  const handleSubmit = () => {
    handleApplyFilters();
  };
  const theme = useStacklineTheme();

  return (
    <>
      <Divider sx={{ borderColor: theme.colors.primaryGray }} />
      <SlRow
        horizontalInset="xl"
        style={{ paddingTop: '20px', paddingBottom: '20px' }}
        spacing="md"
        verticalPosition="center"
        horizontalPosition="space-between"
      >
        <SlButton onClick={handleClearFilters}>Clear</SlButton>
        <SlButton onClick={handleSubmit} variant="contained">
          Apply
        </SlButton>
      </SlRow>
    </>
  );
};

interface FilterSearchInputProps {
  input: string;
  handleChange: (s: string) => void;
  placeholderText?: string;
}
const FilterSearchInput = ({ input, handleChange, placeholderText = '' }: FilterSearchInputProps) => {
  const theme = useStacklineTheme();
  return (
    <TextField
      variant="standard"
      value={input}
      onChange={(event) => handleChange(event.target.value)}
      InputProps={{
        placeholder: placeholderText,
        disableUnderline: true,
        startAdornment: (
          <AppImage
            src="/svg/icons/search.svg"
            alt="search-icon"
            style={{
              marginRight: 10,
              width: '16px',
              height: '16px',
              opacity: 0.5
            }}
          />
        ),
        autoComplete: 'off',
        id: 'type-search'
      }}
      sx={{
        borderRadius: 17,
        border: `1px solid ${theme.colors.primaryGray}`,
        height: 30,
        width: 281,
        backgroundColor: '#fff',
        '& div.MuiInput-root': {
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          height: 30,
          '& input': {
            padding: 0,
            fontSize: '12px',
            fontFamily: 'Roboto',
            color: theme.colors.primary,
            '::placeholder': {
              opacity: 1,
              color: '#BEC7D3'
            }
          }
        }
      }}
    />
  );
};

/**
 * The max options to show/increase by in the filter groups
 */
export const MAX_OPTIONS_SHOWN = 6;

interface AdjustmentStatusOption {
  isSelected: boolean;
  displayName: string;
  label: string;
  id: string;
}

type CategoryOption = CategoryEntity & {
  isSelected: boolean;
};
type SubcategoryOption = SubCategoryEntity & {
  isSelected: boolean;
};
type SegmentOption = Segment & {
  isSelected: boolean;
};

type BrandOption = Partial<BrandEntity> & {
  id: string | number;
  displayName: string;
  isSelected: boolean;
  brandId?: number;
};

type BusinessUnitOption = BusinessUnit & {
  isSelected: boolean;
};

export interface AppFilterGroupProps {
  filterTitle: string;
  options:
    | AdjustmentStatusOption[]
    | CategoryOption[]
    | SubcategoryOption[]
    | SegmentOption[]
    | BrandOption[]
    | BusinessUnitOption[];
  onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isRadio?: boolean;
  placeholderText: string;
  hideSearchInput?: boolean;
}

export const FilterGroup = ({
  filterTitle,
  options,
  onSelect,
  isRadio = false,
  placeholderText = '',
  hideSearchInput = false
}: AppFilterGroupProps) => {
  // Pagination for "Show more" button
  const [optionsPage, setOptionsPage] = useState(1);
  // Input used for searching categories or subcategories
  const [input, setInput] = useState('');
  const theme = useStacklineTheme();

  const userInput = useMemo(() => {
    return input;
  }, [input]);

  /**
   * If the user types in the search bar, we should search all available options.
   * Otherwise we should only show subcategories under a selected parent category.
   * If no category is selected, we can make all options available.
   * Note that we limit (paginate) available options unless the user is searching.
   */
  const availableOptions = useMemo(() => {
    if (userInput.length > 0) {
      return options
        .filter((option) => option.id !== 0 && option.displayName.toLowerCase().includes(userInput.toLowerCase()))
        .map((option) => {
          return {
            id: option.id,
            label: option.displayName,
            isSelected: option.isSelected
          };
        });
    } else {
      return options
        .slice(0, optionsPage * MAX_OPTIONS_SHOWN)
        .filter((cat) => cat.id !== 0)
        .map((option) => {
          return {
            id: option.id,
            label: option.displayName,
            isSelected: option.isSelected
          };
        });
    }
  }, [optionsPage, userInput, options]);

  const showMoreOptions = () => {
    setOptionsPage(optionsPage + 1);
  };

  const showLessOptions = () => {
    setOptionsPage(1);
  };

  // Check if we've shown all options
  const totalOptions = options.filter((opt) => opt.id !== 0).length;
  const hasMoreOptions = totalOptions > optionsPage * MAX_OPTIONS_SHOWN;
  const isExpanded = optionsPage > 1;
  const shouldShowButton = totalOptions > MAX_OPTIONS_SHOWN;

  return (
    <Flex flexDirection="column">
      {/* FilterTitle */}
      <Flex marginTop="24px">
        <Text variant="subtitle1">{filterTitle}</Text>
      </Flex>

      {/* Search Input  */}
      {!hideSearchInput && (
        <Flex marginTop="24px">
          <FilterSearchInput handleChange={setInput} input={input} placeholderText={placeholderText} />
        </Flex>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, auto)',
          color: theme.colors.primary,
          justifyContent: 'space-between',
          rowGap: '16px',
          marginTop: '32px',
          marginBottom: '8px'
        }}
      >
        <>
          {availableOptions ? (
            <>
              {availableOptions.map((option) => (
                <FormControlLabel
                  title={option.label}
                  sx={{
                    gap: '8px',
                    color: theme.colors.primary,
                    minWidth: '190px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginLeft: '0px',
                    marginRight: '0px',
                    '& .MuiTypography-root': {
                      width: '160px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }
                  }}
                  checked={option.isSelected}
                  label={option.label}
                  value={option.id}
                  control={
                    isRadio ? (
                      <Radio
                        disableRipple
                        disableTouchRipple
                        sx={{
                          svg: {
                            height: '15px',
                            width: '15px'
                          },
                          '&, &.Mui-checked': {
                            color: theme.colors.primary
                          }
                        }}
                      />
                    ) : (
                      <StyledCheckbox iconProps={{ width: '14px', height: '14px' }} />
                    )
                  }
                  key={option.id}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSelect(e)}
                />
              ))}
              {/* Prevent justify content with small items */}
              {availableOptions.length < 3 &&
                Array.from({ length: 3 - availableOptions.length }).map((_, index) => (
                  <div key={`placeholder-${index}`} style={{ width: '192px', display: 'hidden' }}></div>
                ))}
            </>
          ) : null}
        </>
      </div>

      {/* Show more/less button */}

      {!hideSearchInput && shouldShowButton ? (
        <button
          style={{
            paddingLeft: 0,
            border: 'none',
            background: 'none',
            textAlign: 'left',
            marginTop: '24px',
            marginBottom: '8px'
          }}
          onClick={isExpanded && !hasMoreOptions ? showLessOptions : showMoreOptions}
        >
          <Text variant="subtitle2" underlined>
            {isExpanded && !hasMoreOptions ? 'Show less' : 'Show more'}
          </Text>
        </button>
      ) : null}
    </Flex>
  );
};

const AppFilterModalInner = ({ history, handleClose }: { history: History; handleClose: () => void }) => {
  const app = useAppSelector((state) => state.app);
  const { tab, subtab } = app.queryParams;
  const filters = useAppSelector((state) => state.filters);
  const filterParamsFromUrl = new URLSearchParams(window.location.search);
  const adjustmentStatusSet = new Set(filterParamsFromUrl.getAll('adjustmentStatus'));
  const [adjustmentSelection, setAdjustmentSelection] = useState(adjustmentStatusSet);
  const categories = useAppSelector((state) => state.categories);
  const subCategories = useAppSelector((state) => state.subCategories);
  const brandsFollowing = useAppSelector((state) => state.brandsFollowing);
  const [selectedBusinessUnitFieldOption, setSelectedBusinessFieldOption] = useState(new Set(filters.businessUnits));
  const [retailPriceRange, setRetailPriceRange] = useState([
    { min: (filters?.retailPrice as any)?.minValue || '' },
    { max: (filters?.retailPrice as any)?.maxValue || '' }
  ]);

  const queryClient = useQueryClient();

  const segments = useAppSelector((state) => state.segments);
  const theme = useStacklineTheme();
  const mainEntityType = useMainEntityType();
  const mainEntityId = useMainEntityId();

  const [quadrants, setQuadrants] = useState<ProductGrowthQuadrant[]>(
    JSON.parse(filterParamsFromUrl.get('filter'))?.quadrants ?? []
  );

  const [matchTypes, setMatchTypes] = useState<VoiceAndToneMatchType[]>(
    JSON.parse(filterParamsFromUrl.get('filter'))?.matchTypes ?? []
  );

  const mainEntity = useAppSelector((state) => state.entityService.mainEntity);

  // TODO: We need to merge mySegments here as well
  const { mySegments, teamSegments, businessUnits, savedSearchesById } = segments;

  const sortByUnitsSold = (a, b) => {
    return b.unitsSold - a.unitsSold;
  };

  // Sort to bring checked items to the front of the collection
  const sortBySelected = (a, b) => {
    return a.isSelected === b.isSelected ? 0 : a.isSelected ? -1 : 1;
  };

  const adjustmentStatusSelectionOption = adjustmentStatusOptions.map((adjustmentStatus) => {
    return {
      ...adjustmentStatus,
      isSelected: adjustmentSelection.has(adjustmentStatus.id),
      displayName: adjustmentStatus.label
    };
  });

  /**
   * A list of available categories to filter on with an additional isSelected property.
   */
  const defaultCategories = _cloneDeep(categories)
    .map((category) => ({
      ...category,
      isSelected: filters.category
        ? !!filters.category.find((categoryFilteredOn) => String(categoryFilteredOn.id) === String(category.id))
        : false
    }))
    .sort(sortByUnitsSold)
    .sort(sortBySelected);

  const [availableCategories, setAvailableCategories] = useState(defaultCategories);

  /**
   * A list of available subcategories to filter on with an additional isSelected property.
   */
  const defaultSubcategories = _cloneDeep(subCategories)
    .map((subcategory) => ({
      ...subcategory,
      isSelected: filters.subcategory
        ? !!filters.subcategory.find(
            (subcategoryFilteredOn) => String(subcategoryFilteredOn.id) === String(subcategory.id)
          )
        : false
    }))
    .sort(sortByUnitsSold)
    .sort(sortBySelected);

  const [availableSubcategories, setAvailableSubcategories] = useState(defaultSubcategories);

  const defaultSegments = [...mySegments, ...teamSegments]
    .sort((a, b) => {
      if (!a.displayName) {
        return 1;
      }
      if (!b.displayName) {
        return -1;
      }
      return a.displayName.localeCompare(b.displayName);
    })
    .map((segment) => ({
      ...segment,
      isSelected: filters.segment ? !!(filters.segment.id === String(segment.id)) : false
    }))
    .sort(sortBySelected);

  const [availableSegments, setAvailableSegments] = useState(defaultSegments);

  // Brands
  const parsedFilter = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return JSON.parse(params.get('filter') || 'null') || {};
    } catch (e) {
      return {};
    }
  })();

  const defaultBrands = useMemo(() => {
    const selectedBrandIdsFromUrl: string[] = (parsedFilter?.b || []).map((item: { i: string | number }) =>
      String(item.i)
    );

    const base = (brandsFollowing || []).filter((brand: any) => brand?.id !== 0);

    return base
      .map((brand: any) => ({
        ...brand,
        id: brand.id,
        displayName: brand.displayName || brand.brandName || brand.name || String(brand.id),
        isSelected: selectedBrandIdsFromUrl.includes(String(brand.id))
      }))
      .sort(sortBySelected as any)
      .sort(sortByLabel as any);
  }, [brandsFollowing, parsedFilter]);

  const [availableBrands, setAvailableBrands] = useState<BrandOption[]>(defaultBrands as BrandOption[]);

  const categoryOptions = useMemo(() => {
    return availableCategories.map((category) => {
      return {
        ...category,
        isSelected: category.isSelected
      };
    });
  }, [availableCategories]);

  const onSelectAdjustmentStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedAdjustmentId = e.target.value;
    const updatedSection = new Set(adjustmentSelection);
    if (updatedSection.has(selectedAdjustmentId)) {
      updatedSection.delete(selectedAdjustmentId);
    } else {
      updatedSection.add(selectedAdjustmentId);
    }
    setAdjustmentSelection(updatedSection);
  };

  const onChangeRetailPriceFilter = (e: React.ChangeEvent<HTMLInputElement>, priceType: 'min' | 'max') => {
    if (priceType === 'min') {
      setRetailPriceRange((prev) => [{ min: e.target.value }, { ...prev[1] }]);
    }
    if (priceType === 'max') {
      setRetailPriceRange((prev) => [{ ...prev[0] }, { max: e.target.value }]);
    }
  };

  const onSelectCategory = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedCategoryId = String(e.target.value);
    const newCategories = categoryOptions.map((category) => {
      return {
        ...category,
        isSelected: selectedCategoryId === String(category.id) ? !category.isSelected : category.isSelected
      };
    });

    setAvailableCategories(newCategories);
    const selectedCategories = newCategories.filter(({ isSelected }) => isSelected);
    if (selectedCategories.length < 1) {
      setAvailableSubcategories(defaultSubcategories.map((subcategory) => ({ ...subcategory, isSelected: false })));
    } else {
      // Clean up subcategories
      const validSubcategoriesById = _flatten(
        selectedCategories.map(({ childEntities }) => childEntities.map(({ id }) => String(id)))
      );
      const validSubcategories = availableSubcategories.map((subcategory) => {
        return {
          ...subcategory,
          isSelected: validSubcategoriesById.includes(String(subcategory.id)) ? subcategory.isSelected : false
        };
      });
      setAvailableSubcategories(validSubcategories);
    }
  };

  const subCategoryOptions = useMemo(() => {
    let selectedCategories = categoryOptions.filter(({ isSelected }) => isSelected);

    if (mainEntity.type === 'category' && String(mainEntity.id) !== '0') {
      selectedCategories = categoryOptions.filter(({ id }) => String(id) === String(mainEntity.id));
    }

    if (selectedCategories.length > 0) {
      const availableSubcategoriesById = _flatten(
        selectedCategories.map(({ childEntities }) => childEntities.map(({ id }) => String(id)))
      );

      return availableSubcategories
        .filter(({ id }) => availableSubcategoriesById.includes(String(id)))
        .map((subcategory) => {
          return {
            ...subcategory,
            isSelected: subcategory.isSelected
          };
        });
    } else {
      return availableSubcategories.map((subcategory) => ({
        ...subcategory,
        isSelected: subcategory.isSelected
      }));
    }
  }, [availableSubcategories, categoryOptions, mainEntity]);

  const onSelectSubcategory = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedSubcategoryId = String(e.target.value);
    const newSubcategories = availableSubcategories.map((subcategory) => {
      return {
        ...subcategory,
        isSelected: selectedSubcategoryId === String(subcategory.id) ? !subcategory.isSelected : subcategory.isSelected
      };
    });
    setAvailableSubcategories(newSubcategories);
  };

  const segmentOptions = useMemo(() => {
    return availableSegments.map((segment) => {
      return {
        ...segment,
        isSelected: segment.isSelected
      };
    });
  }, [availableSegments]);

  const brandOptions = useMemo(() => {
    return availableBrands.map((brand) => ({
      ...brand,
      isSelected: brand.isSelected
    }));
  }, [availableBrands]);

  const onSelectSegment = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedSegmentId = String(e.target.value);
    const newSegments = availableSegments.map((segment) => {
      return {
        ...segment,
        isSelected: selectedSegmentId === String(segment.id)
      };
    });
    setAvailableSegments(newSegments);
  };

  const onSelectBrand = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedBrandId = String(e.target.value);
    const next = availableBrands.map((brand) => ({
      ...brand,
      isSelected: selectedBrandId === String(brand.id) ? !brand.isSelected : brand.isSelected
    }));
    setAvailableBrands(next);
  };

  /**
   * Top level business units for rendering initial load
   */
  const topLevelBusinessUnitsOptions = useMemo(() => {
    // remove duplicated top level bu and filter empty bu
    const topLevelBusinessUnits = _uniqBy(businessUnits, 'id').filter(
      (businessUnit) => businessUnit.isTopLevel && businessUnit.children?.length > 0 && businessUnit.displayName
    );
    return topLevelBusinessUnits;
  }, [businessUnits]);

  const onBusinessUnitSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedBusinessUnitId = String(e.target.value);

    const deleteChildrenEntity = (childId: string) => {
      const node = savedSearchesById.get(childId);
      if (!node) {
        return;
      }

      // segment won't have children
      if (node.type === 'segment') {
        setSelectedBusinessFieldOption((prevSet) => {
          const newOptionSet = new Set(prevSet);
          newOptionSet.delete(childId);
          return newOptionSet;
        });
        return;
      }

      if (node.children.length === 0) {
        return;
      }

      setSelectedBusinessFieldOption((prevSet) => {
        const newOptionSet = new Set(prevSet);
        newOptionSet.delete(childId);
        return newOptionSet;
      });

      for (let i = 0; i < node.children.length; i++) {
        deleteChildrenEntity(node.children[i]);
      }
    };

    if (selectedBusinessUnitFieldOption.has(selectedBusinessUnitId)) {
      // Backend breaks if we pass in a business unit parent without the children.
      if (mainEntityType === 'businessunit' && filters.businessUnits.length === 2) {
        deleteChildrenEntity(mainEntityId as string);
      }
      deleteChildrenEntity(selectedBusinessUnitId);
    } else {
      // Backend breaks if we pass in a business unit children without the parent.
      if (mainEntityType === 'businessunit') {
        setSelectedBusinessFieldOption(
          (prevSet) => new Set([...prevSet, selectedBusinessUnitId, mainEntityId as string])
        );
      }
      setSelectedBusinessFieldOption((prevSet) => new Set([...prevSet, selectedBusinessUnitId]));
    }
  };

  const handleApplyFilters = () => {
    const queryCache = queryClient.getQueryCache();
    const queries = queryCache.getAll();

    queries.forEach((query) => {
      if ((query.state.status as any) === 'loading') {
        query.cancel();
      }
    });

    queryClient.invalidateQueries();
    // Forecasts, adjustment page has special case, that the adjustmentStatus filter url is appending, I think it is intended for using new URLSearchparams getAll
    const adjustmentFilterParams = Array.from(adjustmentSelection.values()).reduce(
      (prevString, currentAdjustmentType) => {
        return `${prevString}&adjustmentStatus=${currentAdjustmentType}`;
      },
      ''
    );

    // Get all selected categories, subcategories, and segments
    const selectedCategories = categoryOptions.filter((category) => category.isSelected);
    const selectedSubcategories = availableSubcategories.filter((subcategory) => subcategory.isSelected);
    const selectedSegments = availableSegments.filter((segment) => segment.isSelected);
    const selectedBrands = brandOptions.filter((brand) => brand.isSelected);
    const selectedBusinessUnit = Array.from(selectedBusinessUnitFieldOption);
    const appliedRetailPrice = validMinMaxRetailPrice(retailPriceRange);

    const filterValues = {
      [FilterKey.Category]: selectedCategories,
      [FilterKey.Subcategory]: selectedSubcategories,
      [FilterKey.Segment]: selectedSegments,
      [FilterKey.Brand]: selectedBrands,
      [FilterKey.Quadrants]: quadrants,
      [FilterKey.MatchTypes]: matchTypes,
      [FilterKey.BusinessUnit]: selectedBusinessUnit,
      [FilterKey.RetailPrice]: appliedRetailPrice
    };

    const { generatedFilterParams } = generateFilterUrlString(filterValues);

    if (generatedFilterParams) {
      filterParamsFromUrl.delete('filter');
      const filterParams = `${filterParamsFromUrl.toString()}${generatedFilterParams}${adjustmentFilterParams}`;

      history.push(`?${filterParams}`);
    } else if (adjustmentFilterParams) {
      filterParamsFromUrl.delete('adjustmentStatus');
      const forecastFilterParams = `${filterParamsFromUrl.toString()}${adjustmentFilterParams}`;
      history.push(`?${forecastFilterParams}`);
    } else {
      filterParamsFromUrl.delete('filter');
      filterParamsFromUrl.delete('adjustmentStatus');
      const url = filterParamsFromUrl.toString();

      history.push(`?${url}`);
    }
    handleClose();
  };

  const clearSelection = () => {
    const clearedCategories = availableCategories.map((category) => ({ ...category, isSelected: false }));
    const clearedSubcategories = availableSubcategories.map((subcategory) => ({ ...subcategory, isSelected: false }));
    const clearedSegments = availableSegments.map((segment) => ({ ...segment, isSelected: false }));
    const clearedBrands = availableBrands.map((brand) => ({ ...brand, isSelected: false }));
    setAdjustmentSelection(new Set());
    setAvailableCategories(clearedCategories);
    setAvailableSubcategories(clearedSubcategories);
    setAvailableSegments(clearedSegments);
    setAvailableBrands(clearedBrands);
    setQuadrants([]);
    setMatchTypes([]);
    setSelectedBusinessFieldOption(new Set());
    setRetailPriceRange([{ min: '' }, { max: '' }]);
  };

  /**
   * Determines whether the filter should be shown for a given entity type. This is to avoid
   * having category filters show when user is viewing a category already.  Applying this filter
   * would not be correct, so we should not give them the option.
   * @param entityType - The type of entity filter to check.
   * @returns Whether the filter should be shown.
   */
  const shouldShowFilter = (entityType: string) => {
    const currentEntityType = mainEntity.type;

    if (
      entityType === 'category' &&
      ['category', 'subcategory'].includes(currentEntityType) &&
      String(mainEntityId) !== '0'
    ) {
      return false;
    }

    if (entityType === 'subcategory' && currentEntityType === 'subcategory') {
      return false;
    }

    if (entityType === 'segment') {
      return currentEntityType !== 'segment' && segmentOptions.length > 0;
    }

    if (entityType === 'businessunit' && currentEntityType === 'businessunit') {
      return false;
    }

    if (entityType === 'brand' && currentEntityType === 'brand') {
      return false;
    }

    return true;
  };

  // Adjustment status is only applicable for one tab -- forecast - adjustment .
  const shouldShowAdjustmentTypeFilter = [BEACON_SUBTABS.FORECAST_ADJUSTMENTS].includes(subtab);
  const shouldShowProductGrowthQuadrantFilter = [BEACON_SUBTABS.GROWTH_MATRIX].includes(subtab);
  const shouldShowVoiceToneQuadrantFilters =
    [BEACON_SUBTABS.CONTENT_VOICE_AND_TONE].includes(subtab) && mainEntityType !== 'product';

  // On businessunit entity, we will show only children of the entity
  const shouldShowFilteredBusinessUnit = mainEntityType === 'businessunit';

  return (
    <>
      <ProductGrowthFilterModalHeader handleClose={handleClose} />
      <div
        className="inner-container"
        style={{ height: '500px', overflow: 'scroll', overflowX: 'hidden', paddingBottom: '35px' }}
      >
        <Flex flexDirection="column" sx={{ paddingLeft: '48px', paddingRight: '48px' }}>
          {shouldShowProductGrowthQuadrantFilter ? (
            <Box marginTop="32px" marginBottom="8px">
              <QuadrantFilterSection quadrants={quadrants} setQuadrants={setQuadrants} />
            </Box>
          ) : null}
          {shouldShowVoiceToneQuadrantFilters ? (
            <Box marginTop="32px" marginBottom="8px">
              <MatchTypeFilterSection
                selectedQuadrants={matchTypes}
                quadrants={[
                  { name: 'Voice Mismatch', id: VoiceAndToneMatchType.VoiceMismatch },
                  { name: 'Full Match', id: VoiceAndToneMatchType.FullMatch },
                  { name: 'Full Mismatch', id: VoiceAndToneMatchType.FullMismatch },
                  { name: 'Tone Mismatch', id: VoiceAndToneMatchType.ToneMismatch }
                ]}
                setQuadrants={setMatchTypes}
              />
            </Box>
          ) : null}
          {shouldShowAdjustmentTypeFilter ? (
            <FilterGroup
              filterTitle="Adjustment Status"
              hideSearchInput
              options={adjustmentStatusSelectionOption}
              onSelect={onSelectAdjustmentStatus}
              placeholderText="Search categories"
            />
          ) : null}

          {/* If business unit entity, show selected bu in filter */}
          {shouldShowFilteredBusinessUnit && (
            <>
              <Text variant="subtitle1" sx={{ marginTop: '24px' }}>
                Business Units
              </Text>
              <BusinessUnitFilterGroup
                key={mainEntityId}
                filterTitle={mainEntity.displayName}
                options={(savedSearchesById.get(mainEntityId as string) as BusinessUnit).children.map((child) => {
                  return { ...savedSearchesById.get(child), isSelected: selectedBusinessUnitFieldOption.has(child) };
                })}
                onSelect={onBusinessUnitSelect}
                isRadio={false}
                placeholderText="Search business units"
                selectedBusinessUnitFieldOption={selectedBusinessUnitFieldOption}
              />
            </>
          )}

          {tab === BEACON_TABS.LIBRARY && shouldShowFilter('brand') ? (
            <FilterGroup
              filterTitle="Brands"
              options={brandOptions as any}
              onSelect={onSelectBrand}
              placeholderText="Search brands"
            />
          ) : null}

          {shouldShowFilter('category') ? (
            <FilterGroup
              filterTitle="Categories"
              options={categoryOptions}
              onSelect={onSelectCategory}
              placeholderText="Search categories"
            />
          ) : null}

          {shouldShowFilter('subcategory') ? (
            <FilterGroup
              filterTitle="Subcategories"
              options={subCategoryOptions}
              onSelect={onSelectSubcategory}
              placeholderText="Search subcategories"
            />
          ) : null}

          {shouldShowFilter('businessunit') && topLevelBusinessUnitsOptions.length > 0 ? (
            <>
              <Text variant="subtitle1" sx={{ marginTop: '24px' }}>
                Business Units
              </Text>
              {topLevelBusinessUnitsOptions
                .sort((a, b) => a.displayName.localeCompare(b.displayName))
                .map((topLevelBusinessUnit) => {
                  const childLevelBusinessUnitOptions = topLevelBusinessUnit.children
                    .map((childBusinessUnit) => {
                      return {
                        ...savedSearchesById.get(childBusinessUnit),
                        isSelected: selectedBusinessUnitFieldOption.has(childBusinessUnit)
                      };
                    })
                    .filter((childBusinessUnit) => childBusinessUnit.id && childBusinessUnit.displayName);

                  if (childLevelBusinessUnitOptions.length > 0) {
                    return (
                      <BusinessUnitFilterGroup
                        key={topLevelBusinessUnit.id}
                        filterTitle={topLevelBusinessUnit.displayName}
                        options={childLevelBusinessUnitOptions}
                        onSelect={onBusinessUnitSelect}
                        isRadio={false}
                        placeholderText="Search business units"
                        selectedBusinessUnitFieldOption={selectedBusinessUnitFieldOption}
                      />
                    );
                  } else {
                    return null;
                  }
                })}
            </>
          ) : null}
          {shouldShowFilter('segment') ? (
            <FilterGroup
              filterTitle="Segments"
              options={segmentOptions}
              onSelect={onSelectSegment}
              isRadio
              placeholderText="Search segments"
            />
          ) : null}
          {shouldShowNewAtlas() && (
            <RetailPriceFilterSection
              onChange={(e, type) => onChangeRetailPriceFilter(e, type)}
              retailPriceRange={retailPriceRange}
            />
          )}
        </Flex>
      </div>
      <AppFilterModalFooter handleClearFilters={clearSelection} handleApplyFilters={handleApplyFilters} />
    </>
  );
};

interface AppFilterModalProps {
  open: boolean;
  handleClose: () => void;
  history: History;
  location: Location;
}
// Export sorting utilities for reuse in AtlasFilterModal
export const sortByUnitsSold = (a: any, b: any) => {
  return (b.unitsSold || 0) - (a.unitsSold || 0);
};

export const sortBySelected = (a: { isSelected?: boolean }, b: { isSelected?: boolean }) => {
  return a.isSelected === b.isSelected ? 0 : a.isSelected ? -1 : 1;
};

export const sortByLabel = (a: { displayName?: string; label?: string }, b: { displayName?: string; label?: string }) =>
  (a.displayName || a.label || '').localeCompare(b.displayName || b.label || '');

export const sortSelectedThenAlpha = <T extends { isSelected?: boolean; displayName?: string; label?: string }>(
  a: T,
  b: T
) => {
  const s = sortBySelected(a, b);
  return s !== 0 ? s : sortByLabel(a, b);
};

// Sort selected items by temporal order, unselected alphabetically
export const sortBySelectionOrder = <
  T extends { id: string | number; isSelected?: boolean; displayName?: string; label?: string }
>(
  items: T[],
  selectionOrder: string[]
): T[] => {
  const selected = items.filter((item) => item.isSelected);
  const unselected = items.filter((item) => !item.isSelected);

  // Sort selected by their order in selectionOrder array
  selected.sort((a, b) => {
    const aIndex = selectionOrder.indexOf(String(a.id));
    const bIndex = selectionOrder.indexOf(String(b.id));
    // If not found in order, put at end
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  // Sort unselected alphabetically
  unselected.sort(sortByLabel);

  return [...selected, ...unselected];
};

export const AppFilterModal = ({ open, handleClose, history }: AppFilterModalProps) => {
  return (
    <GenericStyledDialogue open={open} onClose={handleClose}>
      <AppFilterModalInner handleClose={handleClose} history={history} />
    </GenericStyledDialogue>
  );
};
